/**
 * The postMessage contract between this app and a host page that embeds it in an iframe (the Twilio
 * Flex plugin, or any other integrator). Events don't cross frame boundaries, so without it the
 * inactivity timer cannot see the user working in the host page and signs them out mid-shift.
 *
 * Host -> app:  { type: "scriptly:host-activity", v: 1 }
 *     The user is active in the host page. Send it at most every few seconds while they are.
 * App -> host:  { type: "scriptly:inactivity-warning", v: 1, deadline: <epoch ms of the lock> }
 *     The inactivity warning is showing. Sent only to an origin that has already sent host activity.
 *
 * Host messages are honored only from the direct parent window and only from an allow-listed origin.
 */
export const HostMessageType = {
    Activity: "scriptly:host-activity",
    InactivityWarning: "scriptly:inactivity-warning",
} as const;

export const HOST_MESSAGE_VERSION = 1;

/** Origins trusted to send host activity when appConfig.json names none. Flex serves every account here. */
export const DEFAULT_HOST_ORIGINS: readonly string[] = ["https://flex.twilio.com"];

/** Whether this document is running inside another page's iframe. */
export function isEmbedded(win: Window = window): boolean {
    return win.parent !== win;
}

/**
 * Parses a comma-separated origin list from appConfig.json, falling back to the defaults when it is
 * absent or empty. Entries are normalized to bare origins so a trailing slash or path cannot make a
 * configured host silently fail the exact-match check.
 */
export function parseHostOrigins(raw: string | undefined): readonly string[] {
    const origins = (raw ?? "")
        .split(",")
        .map((entry) => entry.trim())
        .filter(Boolean)
        .flatMap((entry) => {
            try {
                return [new URL(entry).origin];
            } catch {
                console.warn(`Ignoring invalid embed host origin ${JSON.stringify(entry)}.`);
                return [];
            }
        });
    return origins.length ? origins : DEFAULT_HOST_ORIGINS;
}

/** Whether a message event is a well-formed host activity message from a trusted parent. */
export function isHostActivityMessage(event: MessageEvent, win: Window, allowedOrigins: readonly string[]): boolean {
    if (event.source !== win.parent || !allowedOrigins.includes(event.origin)) return false;
    const data = event.data;
    return typeof data === "object" && data !== null
        && data.type === HostMessageType.Activity
        && data.v === HOST_MESSAGE_VERSION;
}

/**
 * Whether the browser reports this frame's direct parent as an allowed origin. Browsers without
 * `location.ancestorOrigins` report nothing, and the host proves itself by sending activity instead.
 */
export function hasTrustedParentOrigin(win: Window, allowedOrigins: readonly string[]): boolean {
    const parentOrigin = win.location?.ancestorOrigins?.[0];
    return parentOrigin !== undefined && allowedOrigins.includes(parentOrigin);
}

export interface HostBridge {
    /** Tells the host the warning is showing, so it can bring it to the user's attention. */
    notifyWarning(deadline: number | null): void;
    /** Whether the parent page is a trusted host: the browser names its origin, or it has sent trusted activity. */
    isTrusted(): boolean;
    stop(): void;
}

/**
 * Listens for host activity and calls `onActivity` for each trusted message. Remembers the origin of
 * the last trusted sender so the warning notice goes back to exactly that host, never to "*": the
 * deadline is harmless, but nothing from this app should be posted to a page it has not verified.
 */
export function createHostBridge(
    win: Window,
    allowedOrigins: readonly string[],
    onActivity: () => void,
): HostBridge {
    let hostOrigin: string | null = null;
    const parentTrusted = hasTrustedParentOrigin(win, allowedOrigins);

    const onMessage = (event: MessageEvent): void => {
        if (!isHostActivityMessage(event, win, allowedOrigins)) return;
        hostOrigin = event.origin;
        onActivity();
    };
    win.addEventListener("message", onMessage);

    return {
        notifyWarning(deadline) {
            if (!hostOrigin) return;
            win.parent.postMessage(
                { type: HostMessageType.InactivityWarning, v: HOST_MESSAGE_VERSION, deadline },
                hostOrigin,
            );
        },
        isTrusted() {
            return parentTrusted || hostOrigin !== null;
        },
        stop() {
            win.removeEventListener("message", onMessage);
        },
    };
}
